/* eslint flowtype/require-valid-file-annotation: 0 */ // bca disabled this, do not copy paste OR ELSE

/**
 * Given a string with unnamed placeholders and an array of JSX elements, iterate
 * over the template string and perform an in-order replacement of placeholders with
 * elements.
 *
 * Return an object containing:
 *  - an array of JSX elements.
 *  - the plaintext string, interpolated
 *
 * To interpolate a string object with JSX objects, we split the template string into fragments,
 * then merge them with JSX elements into a single array.
 */
export default function interpolate(templateString, elements, placeholder = '{}') {
  // split template string into fragments
  const templateFragments = templateString.split(placeholder);

  // Array containing the output JSX, initialized with the first fragment
  const jsxArray = [];
  jsxArray.push(templateFragments.shift());

  // Plaintext output
  let formattedString = templateString;

  // Iterate through the elements, weaving them into the template string
  elements.forEach((element) => {
    // If there is another fragment, push this element and the next fragment onto JSX array.
    // If a template string ends with {} (or the placeholder), the last element in templateFragments is ''
    const templateFragment = templateFragments.shift();
    if (templateFragment || templateFragment === '') {
      jsxArray.push(element);
      jsxArray.push(templateFragment);

      formattedString = formattedString.replace(placeholder, element.props.children);
    }
  });

  return {
    jsxArray,
    plaintext: formattedString,
  };
}

/**
 * This method converts the incoming string into an array of strings, replaces
 * strings matching the marker with args from the interpolation context,
 * then returns the array.
 *
 * @param {string} template - a string containing markers matching keys in the interpolation context.
 * @param {Object} args - the interpolation context. values should be JSX objects.
 * @return {array} strings and JSX elements
 */

function interpolateTemplate(template, args, substitutionMarker) {
  const templateFragments = template.split(substitutionMarker);

  templateFragments.forEach((templateFragment, i) => {
    if (templateFragment.match(substitutionMarker)) {
      // omit braces and whitespace from fragment to get key name
      const keyName = templateFragment.replace(/[\{\}]/g, '').trim();

      if (Object.prototype.hasOwnProperty.call(args, keyName)) {
        templateFragments[i] = args[keyName];
      }
    }
  });

  return templateFragments;
}

/**
 * Perform string interpolation on a format string with markers in a nunjucks-style template.
 *
 * A nunjucks template follows the following format:
 *   `My name is {{ username }}`
 * With named arguments surrounded by two sets of curly braces.
 *
 * Use this as an alternative to the `api_template_fmt` nunjucks filter and `StringUtil.formatApiTemplate`
 *
 * @param {string} template - a string containing markers matching keys in the interpolation context.
 * @param {Object} args - the interpolation context. values should be JSX objects.
 * @return {array} strings and JSX elements
 */

const NAMED_TEMPLATE_SUBSTITUTION_MARKER = /(\{\{\s*\w+\s*\}\})/g;

export function interpolateNamedTemplate(template, args) {
  return interpolateTemplate(template, args, NAMED_TEMPLATE_SUBSTITUTION_MARKER);
}

/**
 * Perform string interpolation on a format string with markers in an API template.
 *
 * An API template follows the following format:
 *   `My name is { username }`
 * With named arguments surrounded by a single set of curly braces.
 *
 * Use this as an alternative to `fmt` and `fmt_link` nunjucks filters, and
 * `StringUtil.format`, `StringUtil.safeFormat`
 *
 * @param {string} template - a string containing markers matching keys in the interpolation context.
 * @param {Object} args - the interpolation context. values should be JSX objects.
 * @return {array} strings and JSX elements
 */

const API_TEMPLATE_SUBSTITUTION_MARKER = /(\{\s*\w+\s*\})/g;

export function interpolateApiTemplate(template, args) {
  return interpolateTemplate(template, args, API_TEMPLATE_SUBSTITUTION_MARKER);
}
